import Layout from "hocs/layouts/Layout";
import Navbar from "components/navigation/Navbar";
import Footer from "components/navigation/Footer";
import { Link, useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import moment from "moment";
import "moment/locale/es";
import DOMPurify from "dompurify";
import axios from "axios";
import { fetchJobDetail } from "utils/apiContent";

const onlyDigits = (value = "") => value.replace(/\D/g, "");
const allowedCvExtensions = [".pdf", ".doc", ".docx"];
const allowedCvMimeTypes = [
	"application/pdf",
	"application/msword",
	"application/vnd.openxmlformats-officedocument.wordprocessingml.document",
];

const getAssetUrl = (path) => {
	if (!path) return "";
	if (String(path).startsWith("http")) return path;
	return `${process.env.REACT_APP_EXTERNAL_API_URL}/${String(path).replace(/^\/+/, "")}`;
};

const initialApplicationForm = {
	full_name: "",
	email: "",
	phone: "",
	cover_letter: "",
	cv: null,
	origin: "externo",
};
const APPLY_LIMIT_STORAGE_KEY = "jobs_public_apply_last_date";
const getTodayLocalDateKey = () => {
	const now = new Date();
	const year = now.getFullYear();
	const month = String(now.getMonth() + 1).padStart(2, "0");
	const day = String(now.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};
const isApplyLockedTodayFromStorage = () => {
	try {
		return window.localStorage.getItem(APPLY_LIMIT_STORAGE_KEY) === getTodayLocalDateKey();
	} catch (error) {
		return false;
	}
};
const clampHtmlToPlainText = (html = "") =>
	String(DOMPurify.sanitize(String(html || ""), { ALLOWED_TAGS: [] }))
		.replace(/&nbsp;/g, " ")
		.replace(/\s+/g, " ")
		.trim();

function JobDetail() {
	const params = useParams();
	const id = params.id;
	const [job, setJob] = useState(null);
	const [applicationForm, setApplicationForm] = useState(initialApplicationForm);
	const [isApplying, setIsApplying] = useState(false);
	const [applyFeedback, setApplyFeedback] = useState({ type: "", message: "" });
	const [applyLockedToday, setApplyLockedToday] = useState(false);
	const [showApplyForm, setShowApplyForm] = useState(false);

	useEffect(() => {
		let mounted = true;
		window.scrollTo(0, 0);
		const load = async () => {
			try {
				const data = await fetchJobDetail(id);
				if (mounted) {
					setJob(data);
				}
			} catch (error) {
				if (mounted) {
					setJob(null);
				}
			}
		};
		load();

		return () => {
			mounted = false;
		};
	}, [id]);

	useEffect(() => {
		setApplyLockedToday(isApplyLockedTodayFromStorage());
	}, [id]);

	const handleInputChange = (event) => {
		const { name, value } = event.target;
		setApplicationForm((prev) => ({
			...prev,
			[name]: name === "phone" ? onlyDigits(value).slice(0, 15) : value,
		}));
	};

	const handleCvChange = (event) => {
		const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;
		setApplicationForm((prev) => ({ ...prev, cv: file }));
	};

	const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

	const isValidCvFile = (file) => {
		if (!file) return false;
		const lowerName = String(file.name || "").toLowerCase();
		const hasValidExtension = allowedCvExtensions.some((ext) => lowerName.endsWith(ext));
		const hasValidMime = !file.type || allowedCvMimeTypes.includes(file.type);
		return hasValidExtension && hasValidMime;
	};

	const handleApplySubmit = async (event) => {
		event.preventDefault();
		setApplyFeedback({ type: "", message: "" });

		const fullName = applicationForm.full_name.trim();
		const email = applicationForm.email.trim().toLowerCase();
		const phone = onlyDigits(applicationForm.phone).slice(0, 15);
		const coverLetter = applicationForm.cover_letter.trim();

		const lockedNow = applyLockedToday || isApplyLockedTodayFromStorage();
		if (lockedNow) {
			setApplyLockedToday(true);
			setApplyFeedback({
				type: "error",
				message: "Ya se realizó una postulación desde este computador hoy. Intenta mañana.",
			});
			return;
		}

		if (!job?.id) {
			setApplyFeedback({ type: "error", message: "No se pudo identificar la vacante." });
			return;
		}
		if (!fullName) {
			setApplyFeedback({ type: "error", message: "El nombre completo es obligatorio." });
			return;
		}
		if (!email || !isValidEmail(email)) {
			setApplyFeedback({ type: "error", message: "Ingresa un correo electrónico válido." });
			return;
		}
		if (!applicationForm.cv) {
			setApplyFeedback({ type: "error", message: "Adjunta tu hoja de vida en PDF o Word." });
			return;
		}
		if (!isValidCvFile(applicationForm.cv)) {
			setApplyFeedback({
				type: "error",
				message: "El archivo debe ser PDF, DOC o DOCX.",
			});
			return;
		}

		const formData = new FormData();
		formData.append("job", String(job.id));
		formData.append("full_name", fullName);
		formData.append("email", email);
		formData.append("cv", applicationForm.cv);
		formData.append("origin", applicationForm.origin || "web_public");
		if (phone) formData.append("phone", phone);
		if (coverLetter) formData.append("cover_letter", coverLetter);

		setIsApplying(true);
		try {
			await axios.post(
				`${process.env.REACT_APP_EXTERNAL_API_URL}/api/public/apply/`,
				formData
			);
			setApplyFeedback({
				type: "success",
				message: "Tu postulación fue enviada correctamente.",
			});
			setApplicationForm(initialApplicationForm);
			setApplyLockedToday(true);
			setShowApplyForm(false);
			try {
				window.localStorage.setItem(APPLY_LIMIT_STORAGE_KEY, getTodayLocalDateKey());
			} catch (storageError) {
				// Si localStorage falla, no bloqueamos la UX.
			}
		} catch (error) {
			const serverMessage =
				error?.response?.data?.detail ||
				error?.response?.data?.error ||
				error?.response?.data?.message;
			setApplyFeedback({
				type: "error",
				message: serverMessage || "No fue posible enviar la postulación. Intenta de nuevo.",
			});
		} finally {
			setIsApplying(false);
		}
	};

	return (
		<Layout>
			<header className="flex justify-center">
				<Navbar />
			</header>
			{job && String(job.id) === String(id) ? (
				<div className="mt-24 mb-4 p-6 rounded-xl grid grid-cols-1 md:grid-cols-12 max-w-7xl mx-auto shadow-2xl">
					<div className="bg-white w-full col-span-5 px-4 rounded-2xl">
						<div className="h-72 md:h-96 rounded-lg overflow-hidden">
							<img
								className="h-full w-full object-cover"
								src={getAssetUrl(job.image)}
								alt="Banner principal de la oferta de trabajo"
							/>
							<div
								className="bg-gray-200 mix-blend-multiply"
								aria-hidden="true"
							/>
						</div>
					</div>
					<div className="col-span-7">
						<h1 className="text-2xl font-bold tracking-tight text-gray-700 sm:text-3xl lg:text-4xl">
							{job.title}
						</h1>
						<div className="mt-4">
							Prioridad: <span className="text-gray-500"> {job.priority}</span>
						</div>
						<div className="flex items-center gap-4 mt-4">
							<div className="w-20 h-20 rounded-lg overflow-hidden">
								<img src={getAssetUrl(job?.user?.picture)} className="h-full w-full object-cover" alt={`Logo de ${job?.user?.enterprise || "empresa"}`} />
							</div>
							<div className="">
								<span className="text-xl font-medium">{job?.user?.enterprise}</span>
								<span className="block text-gray-400">{job?.profile?.address}</span>
							</div>
						</div>
						<div className="flex items-center gap-3">
							Redes sociales:
							<div className="flex gap-4 mt-2">
								{job?.profile?.facebook ? (
									<a href={job.profile.facebook} target="_blank" rel="nofollow noreferrer">
										<svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-brand-facebook" width="24" height="24" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M7 10v4h3v7h4v-7h3l1 -4h-4v-2a1 1 0 0 1 1 -1h3v-4h-3a5 5 0 0 0 -5 5v2h-3" /></svg>
									</a>
								) : ""}
								{job?.profile?.X ? (
									<a href={job.profile.X} target="_blank" rel="nofollow noreferrer">
										<svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-brand-x" width="24" height="24" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 4l11.733 16h4.267l-11.733 -16z" /><path d="M4 20l6.768 -6.768m2.46 -2.46l6.772 -6.772" /></svg>
									</a>
								) : ""}
								{job?.profile?.instagram ? (
									<a href={job.profile.instagram} target="_blank" rel="nofollow noreferrer">
										<svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-brand-instagram" width="24" height="24" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" fill="none" stroke-linecap="round" stroke-linejoin="round"><path stroke="none" d="M0 0h24v24H0z" fill="none"/><path d="M4 4m0 4a4 4 0 0 1 4 -4h8a4 4 0 0 1 4 4v8a4 4 0 0 1 -4 4h-8a4 4 0 0 1 -4 -4z" /><path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" /><path d="M16.5 7.5l0 .01" /></svg>
									</a>
								) : ""}
							</div>
						</div>
						<div className="min-w-0 flex-1 py-4">
							<div className="">
										<p className="my-4 text-lg font-regular text-gray-500 leading-8">
											{clampHtmlToPlainText(job.description)}
										</p>
								<span className="mt-2 text-gray-400 capitalize">
									{moment(job.created).format("MMM D, YYYY")}
								</span>
							</div>
						</div>
					</div>

					<div className="relative overflow-hidden bg-white">
						<div className="relative px-4 sm:px-6 lg:px-8">
							<div className="prose prose-lg max-w-4xl prose-indigo mx-auto text-gray-600">
								<div
									dangerouslySetInnerHTML={{
										__html: DOMPurify.sanitize(job.content),
									}}
								/>
							</div>
						</div>
					</div>

					<div className="col-span-12 mt-8">
						<div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8">
							<div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
								<div className="lg:col-span-5">
									<h2 className="text-2xl font-bold text-gray-800">Postúlate a esta vacante</h2>
										<p className="mt-3 text-gray-500 leading-7">
											Completa tus datos y adjunta tu hoja de vida. Esta postulación se envía directamente a la empresa.
										</p>
										<div className="mt-4 rounded-lg bg-gray-50 p-4 text-sm text-gray-600">
										<p>
											<span className="font-semibold text-gray-700">Vacante:</span> {job.title}
										</p>
										<p className="mt-1">
											<span className="font-semibold text-gray-700">Empresa:</span> {job?.user?.enterprise || "Empresa"}
										</p>
											<p className="mt-1">
												<span className="font-semibold text-gray-700">CV permitido:</span> PDF, DOC o DOCX
											</p>
											<p className="mt-1">
												<span className="font-semibold text-gray-700">Límite:</span> 1 postulación por computador al día
											</p>
										</div>
									</div>

								<div className="lg:col-span-7">
									<div className="rounded-lg border border-green-100 bg-green-50/60 p-4 mb-4">
										<label className="flex items-center gap-2 text-sm font-medium text-gray-700 cursor-pointer">
											<input
												type="checkbox"
												checked={showApplyForm}
												onChange={(event) => {
													const checked = event.target.checked;
													setShowApplyForm(checked);
													if (!checked) {
														setApplyFeedback({ type: "", message: "" });
													}
												}}
												className="h-4 w-4 accent-green-corpo"
											/>
											Quiero postularme
										</label>
										<p className="mt-1 text-xs text-gray-600">
											Marca esta opción para mostrar el formulario de postulación.
										</p>
										{applyLockedToday ? (
											<p className="mt-2 text-xs font-medium text-green-corpo">
												Ya se envió una postulación desde este computador hoy. Podrás enviar otra mañana.
											</p>
										) : null}
									</div>

									{showApplyForm ? (
									<form onSubmit={handleApplySubmit} className="space-y-4">
										<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
											<div>
												<label htmlFor="full_name" className="block text-sm font-medium text-gray-700 mb-1">
													Nombre completo *
												</label>
												<input
													id="full_name"
													name="full_name"
													type="text"
													required
													value={applicationForm.full_name}
													onChange={handleInputChange}
													placeholder="Ej: Juan Pérez"
													className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-corpo focus:border-green-corpo"
												/>
											</div>

											<div>
												<label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
													Correo electrónico *
												</label>
												<input
													id="email"
													name="email"
													type="email"
													required
													value={applicationForm.email}
													onChange={handleInputChange}
													placeholder="correo@ejemplo.com"
													className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-corpo focus:border-green-corpo"
												/>
											</div>
										</div>

										<div>
											<label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
												Teléfono (opcional)
											</label>
											<input
												id="phone"
												name="phone"
												type="text"
												inputMode="numeric"
												maxLength={15}
												value={applicationForm.phone}
												onChange={handleInputChange}
												placeholder="3001234567"
												className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-corpo focus:border-green-corpo"
											/>
										</div>

										<div>
											<label htmlFor="cv" className="block text-sm font-medium text-gray-700 mb-1">
												Hoja de vida (PDF/Word) *
											</label>
											<input
												id="cv"
												name="cv"
												type="file"
												required
												accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
												onChange={handleCvChange}
												className="w-full rounded-lg border border-gray-300 px-4 py-2.5 text-gray-700 file:mr-4 file:rounded-md file:border-0 file:bg-green-50 file:px-4 file:py-2 file:text-sm file:font-medium file:text-green-corpo hover:file:bg-green-100"
											/>
											{applicationForm.cv ? (
												<p className="mt-2 text-xs text-gray-500">Archivo seleccionado: {applicationForm.cv.name}</p>
											) : null}
										</div>

										<div>
											<label htmlFor="cover_letter" className="block text-sm font-medium text-gray-700 mb-1">
												Carta de presentación (opcional)
											</label>
											<textarea
												id="cover_letter"
												name="cover_letter"
												rows="5"
												value={applicationForm.cover_letter}
												onChange={handleInputChange}
												placeholder="Cuéntanos brevemente tu experiencia o por qué aplicas a esta vacante."
												className="w-full rounded-lg border border-gray-300 px-4 py-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-corpo focus:border-green-corpo"
											/>
										</div>

										{applyFeedback.message ? (
											<div
												className={`rounded-lg px-4 py-3 text-sm ${
													applyFeedback.type === "success"
														? "bg-green-50 text-green-700 border border-green-200"
														: "bg-red-50 text-red-700 border border-red-200"
												}`}
											>
												{applyFeedback.message}
											</div>
										) : null}

										<div className="flex items-center justify-between gap-3 flex-wrap">
											<p className="text-xs text-gray-500">
												Al enviar, aceptas compartir tus datos con la empresa.
											</p>
											<button
												type="submit"
												disabled={isApplying || applyLockedToday}
												className="inline-flex items-center justify-center rounded-lg bg-green-corpo px-6 py-3 text-sm font-semibold text-white shadow hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
											>
												{isApplying ? "Enviando..." : applyLockedToday ? "Postulación bloqueada hoy" : "Enviar postulación"}
											</button>
										</div>
									</form>
									) : (
										<div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-6 text-sm text-gray-500">
											El formulario de postulación se mostrará cuando marques la opción <span className="font-medium text-green-corpo">Quiero postularme</span>.
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			) : (
				<>Loading</>
			)}
			<Footer />
		</Layout>
	);
}

export default JobDetail;
